import { PocSwitch } from 'commons/components/components-wrapper/poc-switch.component';
import { PocIconButton } from 'commons/components/components-wrapper/poc-icon-button.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { Input, SearchableDropdown, SearchableDropdownSingle } from 'commons/components';
import { useTranslation } from 'core/i18n/i18n';
import { PredefinedCommentDeviceTypeEntry } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-form/settings-devices-predefined-comments-form.model';
import {
    LocationsSearchableDropdown,
    PredefinedCommentFormCard,
    PredefinedCommentFormCardContent,
    PredefinedCommentFormCardHeader,
    PredefinedCommentFormFieldRow,
    PredefinedCommentFormFieldRowLabel,
    PredefinedCommentPriorityInputWrapper,
} from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-form/settings-devices-predefined-comments-form.style';
import { DeviceTypeDetails } from 'modules/devices/domain/api/device-type.api';
import { useMemo, useState } from 'react';
import {
    mapLocationOptions,
    mapResultTypeOptions,
} from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-form/settings-devices-predefined-comments-form.mapper';
import { Location } from 'core/contexts/locations/location.model';
import { useLocationInitializer } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-form/hooks/use-location-initializer.hook';
import {
    COMMENT_PRIORITY_MAX,
    COMMENT_PRIORITY_MIN,
} from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-form/settings-devices-predefined-comments-form.constants';
import {
    getCommentPriorityValidationMessages,
    validateCommentPriority,
} from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-form/settings-devices-predefined-comments-form.validation';

export interface PredefinedCommentDeviceTypeCardProps {
    entry: PredefinedCommentDeviceTypeEntry;
    deviceTypeOptions: DeviceTypeDetails[];
    onChangeEntry: <K extends keyof PredefinedCommentDeviceTypeEntry>(
        entryId: string,
        field: K,
        value: PredefinedCommentDeviceTypeEntry[K]
    ) => void;
    onRemove: (entryId: string) => void;
    canRemove: boolean;
    locations: Location[];
}

const getResultTypesForDeviceType = (deviceTypes: DeviceTypeDetails[], deviceTypeId?: string) => {
    if (!deviceTypes || !deviceTypeId) {
        return [];
    }

    return deviceTypes.find((dt) => dt.id === deviceTypeId)?.resultTypes || [];
};

export const PredefinedCommentDeviceTypeCard: React.FC<PredefinedCommentDeviceTypeCardProps> = ({
    entry,
    deviceTypeOptions,
    onChangeEntry,
    onRemove,
    canRemove,
    locations,
}) => {
    const { t } = useTranslation();
    const [localResultTypes, setLocalResultTypes] = useState<string[]>(entry.resultTypes);
    const [priorityTouched, setPriorityTouched] = useState<boolean>(entry.order != null);
    const { allLocationIds, localLocations, setLocalLocations } = useLocationInitializer({
        entry,
        locations,
        onChangeEntry,
    });

    const onClear = (field: keyof PredefinedCommentDeviceTypeEntry) => onChangeEntry(entry.id, field, []);

    const handleDeviceTypeChange = (value?: string) => {
        onChangeEntry(entry.id, 'deviceTypeId', value);
        onChangeEntry(entry.id, 'resultTypes', []);
        // Preselected locations should be always all
        onChangeEntry(entry.id, 'locations', allLocationIds);
        setLocalResultTypes([]);
        setLocalLocations(allLocationIds);
    };

    const resultTypeOptions = useMemo(
        () => mapResultTypeOptions(getResultTypesForDeviceType(deviceTypeOptions, entry.deviceTypeId)),
        [deviceTypeOptions, entry.deviceTypeId]
    );

    const locationOptions = useMemo(() => mapLocationOptions(locations), [locations]);

    const priorityValidity = useMemo(
        () => (priorityTouched ? validateCommentPriority(entry.order) : { state: 'valid' as const }),
        [entry.order, priorityTouched]
    );
    const priorityValidationMessages = useMemo(() => getCommentPriorityValidationMessages(t), [t]);

    const handlePriorityChange = (value: string | null) => {
        setPriorityTouched(true);
        if (!value) {
            onChangeEntry(entry.id, 'order', null);
            return;
        }
        const parsed = Number(value);
        onChangeEntry(entry.id, 'order', Number.isInteger(parsed) ? parsed : null);
    };

    return (
        <PredefinedCommentFormCard>
            <PredefinedCommentFormCardContent>
                <PredefinedCommentFormCardHeader>
                    <PocTypography variant="subtitle2">
                        {t('settings.devices.predefined-comments.form.device-type.title')}
                    </PocTypography>
                    <PocIconButton
                        data-testid="delete-button"
                        icon="delete"
                        onClick={() => onRemove(entry.id)}
                        style={{ visibility: canRemove ? 'visible' : 'hidden' }}
                    />
                </PredefinedCommentFormCardHeader>
                <SearchableDropdownSingle
                    name={`device-type-${entry.id}`}
                    size={'small'}
                    searchable
                    placeholder={t('settings.devices.predefined-comments.form.device-type.placeholder')}
                    options={deviceTypeOptions}
                    value={entry?.deviceTypeId}
                    onValueChange={(val) => handleDeviceTypeChange(val)}
                />
                <PredefinedCommentFormFieldRow>
                    <PredefinedCommentFormFieldRowLabel>
                        <PocTypography variant="subtitle2">
                            {t('settings.devices.predefined-comments.form.comment-priority.label')}
                        </PocTypography>
                        <PocTypography variant="caption">
                            {t('settings.devices.predefined-comments.form.comment-priority.description')}
                        </PocTypography>
                    </PredefinedCommentFormFieldRowLabel>
                    <PredefinedCommentPriorityInputWrapper>
                        <Input
                            name={`comment-priority-${entry.id}`}
                            type="number"
                            placeholder="settings.devices.predefined-comments.form.comment-priority.placeholder"
                            minValue={COMMENT_PRIORITY_MIN}
                            maxValue={COMMENT_PRIORITY_MAX}
                            value={entry.order == null ? '' : String(entry.order)}
                            validity={priorityValidity}
                            validationMessages={priorityValidationMessages}
                            floatingAssistiveText
                            onChange={(_, value) => handlePriorityChange(value)}
                        />
                    </PredefinedCommentPriorityInputWrapper>
                </PredefinedCommentFormFieldRow>
                <PocTypography variant="subtitle2">
                    {t('settings.devices.predefined-comments.form.result-types.title')}
                </PocTypography>
                <SearchableDropdown
                    name={`result-type-${entry.id}-${entry.deviceTypeId}`}
                    options={resultTypeOptions}
                    size="small"
                    disabled={!entry.deviceTypeId || resultTypeOptions.length === 0}
                    placeholder={t('settings.devices.predefined-comments.form.result-types.placeholder')}
                    onClickOutside={() => setLocalResultTypes(entry.resultTypes)}
                    value={localResultTypes}
                    onValueChange={(value) => setLocalResultTypes(value)}
                    onClear={() => {
                        setLocalResultTypes([]);
                        onClear('resultTypes');
                    }}
                    onApplySelection={() => onChangeEntry(entry.id, 'resultTypes', localResultTypes)}
                />
                <PredefinedCommentFormFieldRow>
                    <PredefinedCommentFormFieldRowLabel>
                        <PocTypography variant="subtitle2">
                            {t('settings.devices.predefined-comments.form.mark-as-erroneous.label')}
                        </PocTypography>
                        <PocTypography variant="caption">
                            {t('settings.devices.predefined-comments.form.mark-as-erroneous.description')}
                        </PocTypography>
                    </PredefinedCommentFormFieldRowLabel>
                    <PocSwitch
                        data-testid="mark-as-erroneous-switch"
                        checked={entry.erroneous}
                        onValueChange={(ev) => onChangeEntry(entry.id, 'erroneous', ev.detail)}
                    />
                </PredefinedCommentFormFieldRow>
                <PocTypography variant="subtitle2">
                    {t('settings.devices.predefined-comments.form.location.title')}
                </PocTypography>
                <LocationsSearchableDropdown
                    name={`locations-${entry.id}-${entry.deviceTypeId}`}
                    options={locationOptions}
                    size="small"
                    selectAllText={t('settings.devices.predefined-comments.form.location.all')}
                    disabled={!entry.deviceTypeId || locationOptions.length === 0}
                    placeholder={t('settings.devices.predefined-comments.form.location.placeholder')}
                    onClickOutside={() => setLocalLocations(entry.locations)}
                    value={localLocations}
                    onValueChange={(value) => setLocalLocations(value)}
                    onClear={() => {
                        setLocalLocations([]);
                        onClear('locations');
                    }}
                    onApplySelection={() => onChangeEntry(entry.id, 'locations', localLocations)}
                />
            </PredefinedCommentFormCardContent>
        </PredefinedCommentFormCard>
    );
};
